import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, ReplaySubject, tap } from 'rxjs';
import { User } from 'app/core/user/user.types';
import { User as OidcUser } from 'oidc-client';
import { UserSettingsHttpService } from '@feature/user-settings/services/user-settings-http.service';
import { UserSettings } from '@feature/user-settings/models/user-settings';
import { EntityKey } from '@feature/pma-entity/pma-entity-types';

@Injectable({
  providedIn: 'root'
})
export class UserService {
  private _user: ReplaySubject<User> = new ReplaySubject<User>(1);

  /**
   * Constructor
   */
  constructor(private _httpClient: HttpClient,
    private userSettingsHttpService: UserSettingsHttpService) {
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  /**
   * Setter & getter for user
   *
   * @param value
   */
  set user(value: User) {
    // Store the value
    this._user.next(value);
  }

  get user$(): Observable<User> {
    return this._user.asObservable();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------

  retrieveUser(oidcUser: OidcUser) {
    this.userSettingsHttpService.getUserSettings().subscribe((userSettings: UserSettings) => {
      if ( userSettings && oidcUser ) {
        this._user.next(this.mapUser(userSettings, oidcUser));
      }      
    });
  }

  private mapUser(userSettings: UserSettings, oidcUser: OidcUser): User {
    return {
      id: userSettings.MojoPlatformUserId.toString(),
      name: oidcUser.profile.preferred_username,
      email: oidcUser.profile.email,
      linkedKey: this.getLimitedAccessKey(userSettings),
      product: userSettings.Product
    }
  }

  private getLimitedAccessKey(userSettings: UserSettings): EntityKey {
    if (userSettings.LimitedAccess.TenantParent) {
      return userSettings.LimitedAccess.TenantParent;
    }
    else if (userSettings.LimitedAccess.Tenants && userSettings.LimitedAccess.Tenants.length == 1) {
      return userSettings.LimitedAccess.Tenants[0];
    }
    else if(userSettings.LimitedAccess.Occupant){
      return userSettings.LimitedAccess.Occupant;
    }
    return undefined;
  }

  /**
   * Update the user
   *
   * @param user
   */
  update(user: User): Observable<any> {
    return this._httpClient.patch<User>('api/common/user', { user }).pipe(
      map((response) => {
        this._user.next(response);
      })
    );
  }
}
